import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import VitalManualEntryLayout from "../../components/Common/VitalManualEntryLayout";
import {
  SingleVitalInput,
  getVitalInputError,
} from "../../components/Common/VitalInput";
import { submitHealthProfileEntry } from "../../utils/submitHealthProfileEntry";
import { useSubmitVitalForm } from "../../hooks/useSubmitVitalForm";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";

const HEART_RATE_MIN = 25;
const HEART_RATE_MAX = 200;

export interface HeartRateManualEntryProps {
  onBack?: () => void;
  onCancel?: () => void;
  /** Called after save succeeds; may be async (e.g. refetch). Metric screen shows loading until this completes. */
  onSave?: () => void | Promise<void>;
}

export default function HeartRateManualEntry({
  onBack,
  onCancel,
  onSave,
}: HeartRateManualEntryProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState("");

  const handleBack = onBack ?? (() => navigate(-1));
  const handleCancel = onCancel ?? (() => navigate(-1));

  const getSubmitPromise = useCallback(
    () => submitHealthProfileEntry("Pulse", value),
    [value]
  );

  const hasValidationError = getVitalInputError(
    value,
    HEART_RATE_MIN,
    HEART_RATE_MAX
  );

  const { handleSave, isSubmitting } = useSubmitVitalForm({
    getSubmitPromise,
    canSubmit: !!value && value !== "-" && !hasValidationError,
    errorMessage: t(
      "Metric.manualEntry.updateError",
      "Unable to save. Please try again."
    ),
    onSuccess: onSave,
  });

  if (isSubmitting) {
    return <FullscreenLoadingIndicator mainIconType="face" />;
  }

  return (
    <VitalManualEntryLayout
      title={t("HeartRate.manualEntry.title")}
      onBack={handleBack}
      onCancel={handleCancel}
      onSave={handleSave}
      isSaveDisabled={
        !value || value === "-" || isSubmitting || hasValidationError
      }
      cancelText={t("HeartRate.manualEntry.cancel")}
      saveText={t("HeartRate.manualEntry.save")}
    >
      <SingleVitalInput
        value={value}
        onChange={setValue}
        placeholder="72"
        ariaLabel={t("HeartRate.manualEntry.inputLabel")}
        unit={t("HeartRate.unit")}
        min={HEART_RATE_MIN}
        max={HEART_RATE_MAX}
        integerOnly
        errorText={t(
          "HeartRate.manualEntry.validationError",
          "Heart rate must be between 25 and 200 bpm"
        )}
      />
    </VitalManualEntryLayout>
  );
}
